'use client';

import { useState } from 'react';
import { Form } from '@/app/components/shared/Form';
import { SubmitButton } from '@/app/components/shared/SubmitButton';
import { handleLogin } from '@/app/actions/auth';
import { trackEvent, ANALYTICS_EVENTS } from '@/app/lib/analytics';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

// Registration can be controlled via NEXT_PUBLIC_ENABLE_REGISTRATION environment variable
const ENABLE_REGISTRATION = process.env.NEXT_PUBLIC_ENABLE_REGISTRATION === 'true';

export default function LoginForm() {
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  async function onSubmit(formData: FormData) {
    try {
      setError(null);

      // First validate credentials and get the redirect path
      const result = await handleLogin(formData);

      if ('error' in result) {
        setError(result?.error || 'An unexpected error occurred');
        return { error: result.error, success: false };
      }

      // Track a successful login (no PII — email is never sent).
      trackEvent(ANALYTICS_EVENTS.LOGIN, {
        source: 'web',
        method: 'credentials',
      });

      // Return success before navigation to maintain loading state
      const response = { success: true };
      
      // Use window.location for a full page load instead of Next.js router
      // This ensures the session is fully loaded when arriving at the destination
      if (typeof window !== 'undefined') {
        // We already know the credentials are valid and have the correct redirect path
        window.location.href = result.redirectPath;
        // Return early since we're doing a full page navigation
        return response;
      }
      
      // Fallback to Next.js router if window is not available (SSR)
      router.push(result.redirectPath as any);
      return response;
    } catch (e) {
      console.error('Login submission error:', e);
      setError('An unexpected error occurred');
      return { error: 'An unexpected error occurred', success: false };
    }
  }

  return (
    <>
      {error && (
        <div className="flex justify-center w-full">
          <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 mt-2 rounded-md mb-0 block w-80 appearance-none" role="alert">
            <span className="block sm:inline text-sm">{error}</span>
          </div>
        </div>
      )}
      <Form action={onSubmit}>
        <p className="text-right text-[11px]">
          <Link href="/forgot-password" className="text-sourced-black hover:text-sourced-pink transition-colors">
            Forgot password?
          </Link>
        </p>
        <SubmitButton defaultText="Sign in" pendingText="Signing in..." />
        {ENABLE_REGISTRATION && (
          <p className="text-center text-sm text-sourced-black mt-4">
            {"Don't have an account? "}
            <Link href="/register" className="text-sourced-black hover:text-sourced-pink transition-colors">
              Sign up
            </Link>
            {' for free.'}
          </p>
        )}
      </Form>
    </>
  );
}
